import React from 'react';
import { Globe, Check, LogOut, Mail, Shield, RefreshCw } from 'lucide-react';
import { Language } from '../types';

interface CustomerProfileProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  onLogout: () => void;
  isSuperAdmin?: boolean;
  onSwitchRole?: () => void;
}

export const CustomerProfile: React.FC<CustomerProfileProps> = ({ language, setLanguage, onLogout, isSuperAdmin, onSwitchRole }) => {
  const labels: Record<string, Record<Language, string>> = {
    title: { EN: 'Account Settings', KH: 'ព័ត៌មានផ្ទាល់ខ្លួន', ZH: '账户设置', JA: 'アカウント設定', KO: '계정 설정' },
    member: { EN: 'Standard Member', KH: 'សមាជិកស្តង់ដារ', ZH: '标准会员', JA: '標準会員', KO: '일반 회원' },
    joined: { EN: 'Joined Oct 2023', KH: 'បានចូលរួមក្នុងខែតុលា ឆ្នាំ២០២៣', ZH: '2023年10月加入', JA: '2023年10月に加入', KO: '2023년 10월 가입' },
    details: { EN: 'Personal Details', KH: 'ព័ត៌មានលម្អិត', ZH: '个人详情', JA: '個人情報', KO: '개인 정보' },
    prefs: { EN: 'App Preferences', KH: 'ចំណូលចិត្តកម្មវិធី', ZH: '应用偏好', JA: 'アプリ設定', KO: '앱 기본 설정' },
    lang_title: { EN: 'Display Language', KH: 'ភាសាកម្មវិធី', ZH: '显示语言', JA: '表示言語', KO: '表示 언어' },
    lang_sub: { EN: 'Choose your interface language', KH: 'ជ្រើសរើសភាសានៃកម្មវិធី', ZH: '选择您的界面语言', JA: 'インターフェース言語を選択', KO: '사용자 인터페이스 언어 선택' },
    logout: { EN: 'Terminate Session', KH: 'ចាកចេញពីប្រព័ន្ធ', ZH: '终止会话', JA: 'セッションを終了', KO: '세션 종료' },
    note: { 
      EN: '* This will update all technical diagnostic terminology across the app.', 
      KH: '* វានឹងធ្វើបច្ចុប្បន្នភាពពាក្យបច្ចេកទេសរោគវិនិច្ឆ័យទាំងអស់នៅទូទាំងកម្មវិធី។', 
      ZH: '* 这将更新整个应用中的所有技术诊断术语。', 
      JA: '* これにより、アプリ全体のすべての技術的な診断用語が更新されます。', 
      KO: '* 이것은 앱 전체의 모든 기술 진단 용어를 업데이트합니다.' 
    },
  };

  const langs: { code: Language; label: string }[] = [
    { code: 'KH', label: 'ភាសាខ្មែរ' },
    { code: 'EN', label: 'English' },
    { code: 'ZH', label: '简体中文' },
    { code: 'JA', label: '日本語' },
    { code: 'KO', label: '한국어' },
  ];

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-4xl mx-auto animate-in fade-in duration-500">
      <h1 className="text-4xl font-black text-slate-900 tracking-tighter mb-10">{labels.title[language]}</h1>
      
      <div className="bg-white rounded-[3rem] p-10 border border-slate-100 shadow-2xl shadow-slate-200/50">
        {/* User Identity Header */}
        <div className="flex items-center gap-8 mb-12">
          <div className="w-24 h-24 rounded-3xl bg-slate-900 flex items-center justify-center text-4xl font-black text-white shadow-xl shadow-slate-200">
            U
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Standard User</h2>
            <p className="text-slate-500 font-bold flex items-center gap-2">
              <Shield size={14} className="text-violet-500" /> {labels.member[language]} • {labels.joined[language]}
            </p>
          </div>
        </div>
        
        {isSuperAdmin && (
          <section className="mb-12 p-8 bg-violet-600 rounded-[2.5rem] text-white shadow-xl shadow-violet-200">
             <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div>
                   <h3 className="text-xl font-black mb-1 flex items-center gap-2">
                      <Shield size={20} /> Admin Privileges
                   </h3>
                   <p className="text-violet-100 text-sm font-bold opacity-80">You have multi-role testing access enabled.</p>
                </div>
                <button 
                  onClick={onSwitchRole}
                  className="bg-white text-violet-600 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center gap-3 hover:bg-violet-50 transition-all"
                >
                  <RefreshCw size={18} /> Switch App Role
                </button>
             </div>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Personal Details Section */}
          <section className="space-y-6">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">{labels.details[language]}</h3>
            <div className="space-y-4">
              <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100 group transition-all hover:bg-white hover:shadow-xl hover:shadow-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 flex items-center gap-2">
                  <Mail size={12} /> Account Contact
                </span>
                <p className="font-black text-slate-900">Email Address Linked</p>
              </div>
            </div>
          </section>

          {/* Preferences Section */}
          <section className="space-y-6">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">{labels.prefs[language]}</h3>
            <div className="p-8 bg-white border-2 border-slate-50 rounded-3xl space-y-6 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-violet-50 text-violet-600 rounded-2xl">
                  <Globe size={24} />
                </div>
                <div>
                  <h4 className="font-black text-slate-900">{labels.lang_title[language]}</h4>
                  <p className="text-xs font-bold text-slate-400">{labels.lang_sub[language]}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-2">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLanguage(l.code)}
                    className={`py-3 px-6 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border-2 flex items-center justify-between gap-2 active:scale-95 ${
                      language === l.code 
                      ? 'bg-slate-900 border-slate-900 text-white shadow-xl' 
                      : 'bg-white border-slate-100 text-slate-400 hover:border-slate-300'
                    }`}
                  >
                    <span>{l.label}</span>
                    {language === l.code && <Check size={14} />}
                  </button>
                ))}
              </div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest text-center px-4">
                {labels.note[language]}
              </p>
            </div>
          </section>
        </div>

        {/* Action Footer */}
        <div className="pt-8 border-t border-slate-50">
          <button 
            onClick={onLogout}
            className="flex items-center gap-3 text-red-500 font-black uppercase text-xs tracking-widest px-8 py-4 bg-red-50 rounded-2xl hover:bg-red-100 transition-all active:scale-95 shadow-sm"
          >
            <LogOut size={16}/> {labels.logout[language]}
          </button>
        </div>
      </div>
    </div>
  );
};